import React, {
  Fragment,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  memo,
} from 'react';
import { Table } from 'react-bootstrap';
import { useEvalConfig } from '../../hooks';
import { displayName } from '../../../../utilities/conversions';
import officialscoresheetStyles from '../../officialscoresheet.module.css';
import { fullNameToInitialLastName } from '../../../../utilities/conversions';
import { useScoreSheetContext } from './context';
import { getJudgeDisplayContext } from './helpers';
import { RoutineStatus } from '../../../../models';
import JudgeSearchInput from './JudgeSearchInput';
import { useUser } from '../../../../user-provider';
import { getJudgePanel } from '../../../../redux/_constants/evaluator.constants';

const pendingJudgeCache = new Map();

const getPendingCacheKey = (session, apparatus) => {
  const sessionId =
    session?.id || session?.sessionId || session?.key || 'unknown-session';
  const apparatusKey = apparatus || 'unknown-apparatus';
  return `${sessionId}::${apparatusKey}`;
};

const getPendingCache = (cacheKey) => {
  if (!cacheKey) {
    return { assignments: {}, removals: {} };
  }
  return pendingJudgeCache.get(cacheKey) || { assignments: {}, removals: {} };
};

const setPendingCache = (cacheKey, assignments, removals) => {
  if (!cacheKey) {
    return;
  }
  const hasAssignments = Object.keys(assignments || {}).length > 0;
  const hasRemovals = Object.keys(removals || {}).length > 0;
  if (!hasAssignments && !hasRemovals) {
    pendingJudgeCache.delete(cacheKey);
    return;
  }
  pendingJudgeCache.set(cacheKey, { assignments, removals });
};

// Loading spinner component - defined outside to prevent recreation on each render
const LoadingSpinner = memo(function LoadingSpinner({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="10"
      height="10"
      className={className}
      style={{
        position: 'absolute',
        right: '6pt',
        top: '50%',
        zIndex: 10,
      }}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="#ccc"
        strokeWidth="3"
        opacity="0.5"
      />
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="#00a2ff"
        strokeWidth="3"
        strokeDasharray="15 50"
        strokeLinecap="round"
      />
    </svg>
  );
});

export function ScoreSheetLineup({
  squad,
  showSidebar = true,
  apparatus,
  max,
  isIndividuals,
  teamsByAthleteId,
}) {
  const {
    apparatusName,
    apparatusNameSpan,
    orderNum,
    score,
    scoreDiff,
    scoreExec,
    scoreE,
    name,
    nameTd,
    finalScore,
    scoreSB,
    scoreND,
    scoreUTL,
  } = officialscoresheetStyles;
  const data = (!!squad && squad.slice(0, max)) || [];
  const rows = [...data, ...new Array(max - data.length).fill({})];
  const evalConfig = useEvalConfig();

  const {
    dPanel,
    ePanel,
    jPanel,
    nDeduct,
    startValue,
    stickBonus,
    utl,
    utlPanel,
    svPanel,
  } = evalConfig;
  const showSv = !!utl && Array.isArray(svPanel) && svPanel.length > 0;

  // Show tricodes only when athletes from multiple teams are on this sheet
  const showTriCode = (() => {
    if (!isIndividuals || !teamsByAthleteId) return false;
    const teamIds = new Set();
    for (const r of data) {
      const t = teamsByAthleteId[r.athleteId];
      if (t) teamIds.add(t.id || t.triCode || t.name);
      if (teamIds.size > 1) return true;
    }
    return false;
  })();

  const scoreRow = (r, index) => {
    const team = teamsByAthleteId?.[r.athleteId];
    const teamName = team?.triCode || displayName(team?.name, team?.altNames);
    const teamNameStr = showTriCode ? ` (${teamName})` : '';
    const athleteName = r?.name ? `${r?.name}${teamNameStr}` : '';

    let statusIndicator = null;
    if (r?.status === RoutineStatus.ON_AIR) {
      statusIndicator = (
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#28a745',
            marginRight: '4px',
            verticalAlign: 'middle',
          }}
        ></span>
      );
    } else if (r?.status === RoutineStatus.ON_EVAL) {
      statusIndicator = (
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#ffc107',
            marginRight: '4px',
            verticalAlign: 'middle',
          }}
        ></span>
      );
    }

    return (
      <tr key={`${r.name}_${index}`}>
        <td className={orderNum}>{index + 1}</td>
        <td className={nameTd}>
          <div className={name}>
            <span>{athleteName}</span>
          </div>
        </td>
        {!startValue
          ? dPanel?.map((d) => (
              <td key={d.type} className={scoreDiff}>
                {r?.scores?.[d.type]}
              </td>
            ))
          : null}
        {!!ePanel && ePanel.length > 0 ? (
          <td key="Eavg" className={scoreExec}>
            {r?.scores?.scoreEAverage}
          </td>
        ) : null}
        {ePanel?.map((e) => (
          <td key={e.type} className={scoreE}>
            {r?.scores?.[e.type]}
          </td>
        ))}
        {showSv
          ? Array.from({
              length: Math.max(svPanel.length, jPanel?.length || 0),
            }).map((_, i) => {
              const sv = svPanel?.[i];
              const j = jPanel?.[i];
              return (
                <Fragment key={`svj-${i}`}>
                  {sv ? (
                    <td className={score}>{r?.scores?.[sv.type]}</td>
                  ) : null}
                  {j ? <td className={score}>{r?.scores?.[j.type]}</td> : null}
                </Fragment>
              );
            })
          : jPanel?.map((j) => (
              <td key={j.type} className={score}>
                {r?.scores?.[j.type]}
              </td>
            ))}
        {utl ? (
          <td className={scoreUTL}>
            {(() => {
              const aggregateUtl = r?.scores?.UTL;
              if (typeof aggregateUtl === 'string' && aggregateUtl.trim()) {
                const digits = aggregateUtl.match(/\d+/g);
                if (digits && digits.length) {
                  return digits.join(' ');
                }
                return aggregateUtl;
              }

              if (utlPanel && utlPanel.length) {
                const checkedIndices = utlPanel
                  .map((u, idx) => {
                    const val = r?.scores?.[u.type];
                    return val !== undefined && val !== null && val !== ''
                      ? idx + 1
                      : null;
                  })
                  .filter(Boolean);
                if (checkedIndices.length) {
                  return checkedIndices.join(' ');
                }
              }

              if (aggregateUtl) {
                return '✓';
              }

              return '';
            })()}
          </td>
        ) : null}
        {nDeduct ? <td className={scoreND}>{r?.scores?.ND}</td> : null}
        {stickBonus ? <td className={scoreSB}>{r?.scores?.SB}</td> : null}
        <td className={finalScore}>
          {!r?.finalScore && statusIndicator}
          {r?.finalScore}
        </td>
      </tr>
    );
  };

  return (
    <Table>
      <tbody>
        <tr>
          {showSidebar ? (
            <th className={apparatusName} rowSpan={rows.length + 1}>
              <span className={apparatusNameSpan}>{apparatus}</span>
            </th>
          ) : null}
          <th className={orderNum}>#</th>
          <th className={nameTd}>Name</th>
          {!startValue ? (
            dPanel.length === 1 ? (
              <th key="Diff." className={scoreDiff}>
                Diff.
              </th>
            ) : (
              dPanel?.map((d) => (
                <th key={d.type} className={scoreDiff}>
                  {d.type}
                </th>
              ))
            )
          ) : null}
          {!!ePanel && ePanel.length > 0 ? (
            <th key="Exec." className={scoreExec}>
              Exec.
            </th>
          ) : null}
          {ePanel?.map((e) => (
            <th key={e.type} className={scoreE}>
              {e.type}
            </th>
          ))}
          {showSv
            ? Array.from({
                length: Math.max(svPanel.length, jPanel?.length || 0),
              }).map((_, i) => {
                const sv = svPanel?.[i];
                const j = jPanel?.[i];
                return (
                  <Fragment key={`svj-header-${i}`}>
                    {sv ? <th className={score}>{sv.type}</th> : null}
                    {j ? <th className={score}>{j.type}</th> : null}
                  </Fragment>
                );
              })
            : jPanel?.map((j) => (
                <th key={j.type} className={score}>
                  {j.type}
                </th>
              ))}
          {utl ? <th className={scoreUTL}>UTL</th> : null}
          {nDeduct ? <th className={scoreND}>ND</th> : null}
          {stickBonus ? <th className={scoreSB}>SB</th> : null}
          <th className={finalScore}>Score</th>
        </tr>
        {rows.map((r, i) => scoreRow(r, i))}
      </tbody>
    </Table>
  );
}

// Original table-style judge lineup (preserved for reuse)
export function ScoreSheetJudgeLineupTable({
  judges,
  showSidebar = true,
  apparatus,
  max,
  gender,
}) {
  const {
    apparatusName,
    apparatusNameSpan,
    orderNum,
    name,
    nameTdJudge,
    judgeRating,
  } = officialscoresheetStyles;

  const evalConfig = useEvalConfig();
  const { jPanel } = evalConfig;
  const { hideJudgeNames } = useScoreSheetContext();

  const dataFlat = [judges?.headJudge, ...Object.values(judges?.rest)].filter(
    Boolean
  );

  // Place judges in slots based on their scoreType matching jPanel positions
  // This ensures judges stay in their assigned slots when others are removed
  const numJudges = jPanel?.length || 2;
  const data = new Array(numJudges).fill(null);
  for (const judge of dataFlat) {
    for (let i = 0; i < numJudges; i++) {
      if (judge?.scoreTypes?.includes(jPanel?.[i]?.type)) {
        data[i] = judge;
        break;
      }
    }
  }

  const rows = [...data, ...new Array(Math.max(0, max - data.length)).fill({})];

  const scoreRow = (r, index) => {
    return (
      <tr key={`${index}`}>
        <td className={orderNum}>{index + 1}</td>
        <td className={nameTdJudge}>
          <div className={name}>
            <span>
              {!hideJudgeNames && r?.sessionJudgeAssignment?.judge?.name}
            </span>
          </div>
        </td>
        <td className={judgeRating}>
          {!hideJudgeNames && getJudgeDisplayContext(r, gender, false)}
        </td>
      </tr>
    );
  };

  return (
    <Table>
      <tbody>
        <tr>
          {showSidebar ? (
            <th className={apparatusName} rowSpan={rows.length + 1}>
              <span className={apparatusNameSpan}>{apparatus}</span>
            </th>
          ) : null}
          <th className={orderNum}>#</th>
          <th className={nameTdJudge}>Name</th>
          <th className={judgeRating}>Rating</th>
        </tr>
        {rows.map((r, i) => scoreRow(r, i))}
      </tbody>
    </Table>
  );
}

// Signature-style judge lineup (J1, J2, etc. with signature lines)
// Each judge gets 3 rows: 2 for signature area (no internal borders) + 1 for name/rating
export function ScoreSheetJudgeLineup({
  judges,
  showSidebar = true,
  apparatus,
  max,
  gender,
  editable = false,
  session,
  onJudgeAssign,
  onJudgeRemove,
  onBulkJudgeAssign,
}) {
  const {
    apparatusName,
    apparatusNameSpan,
    orderNum,
    name,
    nameTdJudge,
    judgeTable,
    judgeTopline,
    judgeFiller,
    judgeRating,
    judgeInfo,
    sigTop,
    sigBottom,
    judgeNameClickable,
    judgeRemoveBtn,
    judgeSearchWrapper,
    spinner,
  } = officialscoresheetStyles;

  // State for tracking which judge slot is being edited
  const [editingIndex, setEditingIndex] = useState(null);
  // Ref to track when we're intentionally advancing to next slot (to prevent blur from closing)
  const isAdvancingRef = useRef(false);
  // Optimistic state for pending assignments/removals (can have multiple)
  const cacheKey = useMemo(() => getPendingCacheKey(session, apparatus), [
    session,
    apparatus,
  ]);
  const [pendingAssignments, setPendingAssignments] = useState(
    () => getPendingCache(cacheKey).assignments
  ); // { [index]: judgeName }
  const [pendingRemovals, setPendingRemovals] = useState(
    () => getPendingCache(cacheKey).removals
  ); // { [index]: true }

  // Refs to access latest values without adding to callback dependencies
  const pendingAssignmentsRef = useRef(pendingAssignments);
  useEffect(() => {
    pendingAssignmentsRef.current = pendingAssignments;
  }, [pendingAssignments]);

  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true);

  const evalConfig = useEvalConfig();
  const judgePanel = getJudgePanel(evalConfig);
  const isFigPanel =
    !evalConfig?.jPanel?.length && evalConfig?.dPanel?.length > 0;
  const { hideJudgeNames } = useScoreSheetContext();
  const { isAdmin } = useUser();

  useEffect(() => {
    isMountedRef.current = true;
    console.debug('[ScoreSheetJudgeLineup] mount', { cacheKey });
    return () => {
      isMountedRef.current = false;
      console.debug('[ScoreSheetJudgeLineup] unmount', { cacheKey });
    };
  }, [cacheKey]);

  // Use judgePanel length to determine number of judge slots
  const numJudges = judgePanel?.length || 2;
  const isLargePanel = numJudges >= 4; // J4 or J6 panels

  // Memoize data array to prevent unnecessary re-renders
  // Dependencies: judges object and judgePanel configuration
  const data = useMemo(() => {
    const dataFlat = [
      judges?.headJudge,
      ...Object.values(judges?.rest || {}),
    ].filter(Boolean);

    // Place judges in slots based on their scoreType matching judgePanel positions
    // This ensures judges stay in their assigned slots when others are removed
    // (e.g., J2 stays in slot 2 even if J1 is removed)
    const result = new Array(numJudges).fill(null);
    for (const judge of dataFlat) {
      for (let i = 0; i < numJudges; i++) {
        if (judge?.scoreTypes?.includes(judgePanel?.[i]?.type)) {
          result[i] = judge;
          break;
        }
      }
    }
    return result;
  }, [judges?.headJudge, judges?.rest, numJudges, judgePanel]);

  // Keep ref in sync with data for use in callbacks
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Memoize judgeRows to prevent unnecessary re-renders
  const judgeRows = useMemo(() => {
    // Build rows: each judge gets 3 rows (or 1 row for large panels)
    // Row 1: number + judge name + rating (from DB)
    // Rows 2-3: merged signature area (no internal borders) - hidden for J4/J6
    const rows = [];
    for (let i = 0; i < numJudges; i++) {
      const judge = data[i];
      const judgeName = judge?.sessionJudgeAssignment?.judge?.name;
      const judgeRatingDisplay = getJudgeDisplayContext(
        judge,
        gender,
        false,
        isLargePanel
      );

      // Row 1: number/label + judge name + rating
      rows.push({
        type: 'info',
        label: isFigPanel ? judgePanel[i]?.type : i + 1,
        judgeName,
        judgeRatingDisplay,
        index: i,
      });
      // Only add signature rows for small panels (J2)
      if (!isLargePanel) {
        // Row 2: signature area top (no bottom border)
        rows.push({
          type: 'sig-top',
          index: i,
        });
        // Row 3: signature area bottom (no top border)
        rows.push({
          type: 'sig-bottom',
          index: i,
        });
      }
    }

    // Pad with empty rows to match athlete table height (only for small panels)
    if (!isLargePanel) {
      while (rows.length < max) {
        rows.push({ type: 'empty' });
      }
    }
    return rows;
  }, [data, numJudges, isLargePanel, max, gender]);

  // Memoize excludeJudgeIds to prevent JudgeSearchInput from re-rendering
  const excludeJudgeIds = useMemo(
    () =>
      data
        .filter((j) => j?.sessionJudgeAssignment?.judge?.id)
        .map((j) => j.sessionJudgeAssignment.judge.id),
    [data]
  );

  useEffect(() => {
    const cached = getPendingCache(cacheKey);
    setPendingAssignments(cached.assignments);
    setPendingRemovals(cached.removals);
  }, [cacheKey]);

  useEffect(() => {
    setPendingCache(cacheKey, pendingAssignments, pendingRemovals);
  }, [cacheKey, pendingAssignments, pendingRemovals]);

  // Clear pending assignments when data reflects the change
  useEffect(() => {
    const indicesToClear = [];
    for (const indexStr of Object.keys(pendingAssignments)) {
      const idx = parseInt(indexStr, 10);
      const judgeAtIndex = data[idx];
      const actualName = judgeAtIndex?.sessionJudgeAssignment?.judge?.name;
      if (actualName) {
        indicesToClear.push(indexStr);
      }
    }
    if (indicesToClear.length > 0) {
      setPendingAssignments((prev) => {
        const next = { ...prev };
        indicesToClear.forEach((k) => delete next[k]);
        return next;
      });
    }
  }, [data, pendingAssignments]);

  // Clear pending removals when data reflects the change
  useEffect(() => {
    const indicesToClear = [];
    for (const indexStr of Object.keys(pendingRemovals)) {
      const idx = parseInt(indexStr, 10);
      const judgeAtIndex = data[idx];
      const actualName = judgeAtIndex?.sessionJudgeAssignment?.judge?.name;
      if (!actualName) {
        indicesToClear.push(indexStr);
      }
    }
    if (indicesToClear.length > 0) {
      setPendingRemovals((prev) => {
        const next = { ...prev };
        indicesToClear.forEach((k) => delete next[k]);
        return next;
      });
    }
  }, [data, pendingRemovals]);

  const handleJudgeSelect = useCallback(
    (judgeIndex, judge) => {
      // Get the score type for this judge slot from judgePanel
      const scoreType = judgePanel?.[judgeIndex]?.type || `J${judgeIndex + 1}`;

      // Optimistic update - show judge name immediately
      setPendingAssignments((prev) => ({ ...prev, [judgeIndex]: judge.name }));

      // Find the next empty slot to allow quick tabbing through judges
      // Look for a slot that has no judge assigned and no pending assignment
      // Use refs to get latest values without triggering callback recreation
      let nextEmptyIndex = null;
      const currentData = dataRef.current;
      const currentPending = pendingAssignmentsRef.current;
      for (let i = judgeIndex + 1; i < numJudges; i++) {
        const hasJudge = !!currentData[i]?.sessionJudgeAssignment?.judge?.name;
        const hasPending = !!currentPending[i];
        if (!hasJudge && !hasPending) {
          nextEmptyIndex = i;
          break;
        }
      }

      // Mark that we're intentionally advancing (prevents blur from closing)
      if (nextEmptyIndex !== null) {
        isAdvancingRef.current = true;
        // Reset the flag after blur handler timeout (100ms) has passed
        setTimeout(() => {
          isAdvancingRef.current = false;
        }, 150);
      }

      // Advance to next empty slot (or close if none left)
      setEditingIndex(nextEmptyIndex);

      if (onJudgeAssign) {
        // Don't await - let mutation run in background so user can add more judges quickly
        // The optimistic UI already shows the pending name
        onJudgeAssign(judgeIndex, judge, scoreType, apparatus).catch(() => {
          // On error, clear pending state (only if still mounted)
          if (isMountedRef.current) {
            setPendingAssignments((prev) => {
              const next = { ...prev };
              delete next[judgeIndex];
              return next;
            });
          }
        });
        // Don't clear here - useEffect clears when data arrives
      }
    },
    [judgePanel, apparatus, onJudgeAssign, numJudges]
  );

  const handleJudgeRemove = useCallback(
    async (judgeIndex, judge) => {
      const sessionJudgeAssignment = judge?.sessionJudgeAssignment;
      if (onJudgeRemove && sessionJudgeAssignment) {
        // Optimistic update - show removing state
        setPendingRemovals((prev) => ({ ...prev, [judgeIndex]: true }));
        // Also clear any pending assignment so it doesn't reappear
        setPendingAssignments((prev) => {
          const next = { ...prev };
          delete next[judgeIndex];
          return next;
        });

        try {
          await onJudgeRemove(
            judgeIndex,
            sessionJudgeAssignment.id,
            sessionJudgeAssignment._version
          );
        } catch {
          // On error, clear pending state (only if still mounted)
          if (isMountedRef.current) {
            setPendingRemovals((prev) => {
              const next = { ...prev };
              delete next[judgeIndex];
              return next;
            });
          }
        }
        // Don't clear here - useEffect clears when data arrives
      }
    },
    [onJudgeRemove]
  );

  // Memoize blur handler to prevent recreation on every render
  // Only close if we're not intentionally advancing to the next slot
  // Use a small delay to allow for clicks within the typeahead (e.g., dropdown selection)
  const handleBlur = useCallback((e) => {
    // Don't close immediately - wait to see if focus moves within the typeahead
    setTimeout(() => {
      if (!isAdvancingRef.current) {
        // Check if focus is still within a typeahead component
        const activeEl = document.activeElement;
        const isInTypeahead = activeEl?.closest('.rbt');
        if (!isInTypeahead) {
          setEditingIndex(null);
        }
      }
    }, 100);
  }, []);

  // Handle bulk paste of judge assignments from a table
  const handleBulkPaste = useCallback(
    (assignments) => {
      if (!onBulkJudgeAssign || assignments.length === 0) return;

      // Close the editing input
      setEditingIndex(null);
      // Pass ALL assignments to parent for bulk processing
      onBulkJudgeAssign(assignments, apparatus);
    },
    [apparatus, onBulkJudgeAssign]
  );

  const scoreRow = (row, index) => {
    if (row.type === 'info') {
      const judge = data[row.index];
      const hasJudge = !!row.judgeName;
      const isEditing = editingIndex === row.index;
      const pendingName =
        editable && isAdmin ? pendingAssignments[row.index] : null;
      const isPendingRemove =
        editable && isAdmin && !!pendingRemovals[row.index];

      // Determine what to show in the name cell
      let nameContent;

      if (isPendingRemove) {
        // Show faded state while removing
        nameContent = (
          <>
            <span style={{ opacity: 0.4 }}>{row.judgeName}</span>
            <LoadingSpinner className={spinner} />
          </>
        );
      } else if (pendingName && !hasJudge) {
        // Show optimistic judge name (slightly dimmed until confirmed)
        nameContent = (
          <>
            <span style={{ opacity: 0.85 }}>{pendingName}</span>
            <LoadingSpinner className={spinner} />
          </>
        );
      } else if (editable && isAdmin && isEditing) {
        // Show search input when editing
        nameContent = (
          <div className={judgeSearchWrapper}>
            <JudgeSearchInput
              key={`judge-search-${apparatus}-${row.index}`}
              gender={gender}
              onSelect={(selectedJudge) =>
                handleJudgeSelect(row.index, selectedJudge)
              }
              excludeJudgeIds={excludeJudgeIds}
              placeholder="Type or paste table..."
              autoFocus
              onBlur={handleBlur}
              onBulkPaste={handleBulkPaste}
            />
          </div>
        );
      } else if (editable && isAdmin && !hasJudge) {
        // Show clickable placeholder for empty slot
        nameContent = (
          <span
            className={judgeNameClickable}
            onClick={() => setEditingIndex(row.index)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setEditingIndex(row.index)}
          >
            + Assign
          </span>
        );
      } else if (hasJudge) {
        // Show judge name with optional remove button
        nameContent = (
          <>
            <span>{!hideJudgeNames && row.judgeName}</span>
            {editable && isAdmin && !hideJudgeNames && (
              <button
                type="button"
                className={judgeRemoveBtn}
                onClick={() => handleJudgeRemove(row.index, judge)}
                title="Remove judge"
              >
                ×
              </button>
            )}
          </>
        );
      } else {
        // Non-editable empty slot
        nameContent = <span></span>;
      }

      // First row: number, judge name, rating
      return (
        <tr key={`info-${row.index}`} className={judgeTopline}>
          <td className={`${orderNum} ${judgeInfo}`}>{row.label}</td>
          <td className={`${nameTdJudge} ${judgeInfo}`}>
            <div className={name}>{nameContent}</div>
          </td>
          <td
            className={`${judgeRating} ${judgeInfo}`}
            style={isLargePanel ? { textAlign: 'right' } : undefined}
          >
            {!hideJudgeNames && row.judgeRatingDisplay}
          </td>
        </tr>
      );
    } else if (row.type === 'sig-top') {
      // Second row: signature area top, no bottom border
      return (
        <tr key={`sig-top-${row.index}`} className={judgeFiller}>
          <td className={`${orderNum} ${sigTop}`}></td>
          <td className={`${nameTdJudge} ${sigTop}`}>
            <div className={name}>
              <span></span>
            </div>
          </td>
          <td className={`${judgeRating} ${sigTop}`}></td>
        </tr>
      );
    } else if (row.type === 'sig-bottom') {
      // Third row: signature area bottom, no top border
      return (
        <tr key={`sig-bottom-${row.index}`} className={judgeFiller}>
          <td className={`${orderNum} ${sigBottom}`}></td>
          <td className={`${nameTdJudge} ${sigBottom}`}>
            <div className={name}>
              <span></span>
            </div>
          </td>
          <td className={`${judgeRating} ${sigBottom}`}></td>
        </tr>
      );
    } else {
      return (
        <tr key={`empty-${index}`} className={judgeFiller}>
          <td className={orderNum}></td>
          <td className={nameTdJudge}>
            <div className={name}>
              <span></span>
            </div>
          </td>
          <td className={judgeRating}></td>
        </tr>
      );
    }
  };

  return (
    <Table className={judgeTable}>
      <tbody>
        <tr>
          {showSidebar ? (
            <th className={apparatusName} rowSpan={judgeRows.length + 1}>
              <span className={apparatusNameSpan}>{apparatus}</span>
            </th>
          ) : null}
          <th className={orderNum}>#</th>
          <th className={nameTdJudge}>Judge</th>
          <th className={judgeRating}>Rating</th>
        </tr>
        {judgeRows.map((row, i) => scoreRow(row, i))}
      </tbody>
    </Table>
  );
}

export function ScoreSheetAA({
  data = [],
  showSidebar = true,
  apparatus,
  max,
}) {
  const {
    apparatusName,
    aaOrderNum,
    aaScore,
    aaScoreTd,
    apparatusNameSpan,
    aaTable,
    aaSidebarCol,
    aaOrderNumCol,
  } = officialscoresheetStyles;

  const rows = ['name', ...apparatus, 'AA'];
  const columns = [
    ...data.slice(0, max),
    ...new Array(max - data.length).fill({}),
  ];

  // Show tricodes only when athletes from multiple teams are present
  const showTriCode = (() => {
    const teamIds = new Set();
    for (const athlete of data) {
      if (athlete?.triCode) teamIds.add(athlete.triCode);
      if (teamIds.size > 1) return true;
    }
    return false;
  })();

  const aaRow = (r, data, index) => {
    return (
      <tr key={`${r}_${index}`}>
        {index === 0 ? (
          <th className={aaOrderNum}>#</th>
        ) : (
          <td className={aaOrderNum}>{r}</td>
        )}
        {columns.map((athlete, i) => {
          if (index === 0) {
            return (
              <th key={i} className={aaScoreTd}>
                <div className={aaScore}>
                  <span>
                    {fullNameToInitialLastName(athlete?.name)}
                    {showTriCode && athlete?.triCode
                      ? ` (${athlete.triCode})`
                      : ''}
                  </span>
                </div>
              </th>
            );
          }
          return (
            <td key={i} className={aaScoreTd}>
              <span>{athlete?.scores?.[r]?.score}</span>
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <Table className={aaTable}>
      <colgroup>
        {showSidebar ? <col className={aaSidebarCol} /> : null}
        <col className={aaOrderNumCol} />
        {columns.map((_, i) => (
          <col key={i} />
        ))}
      </colgroup>
      <tbody>
        <tr>
          {showSidebar ? (
            <th className={apparatusName} rowSpan={rows.length + 1}>
              <span className={apparatusNameSpan}>AA</span>
            </th>
          ) : null}
        </tr>
        {rows.map((a, i) => aaRow(a, data, i))}
      </tbody>
    </Table>
  );
}
